import { lazy, Suspense } from 'react';

import { Loader } from '@crehana/compass.ui';
import { Dialog, DialogContent, DialogTitle } from '@crehana/compass.ui/v2';

import type { TDocumentPreview } from './types';
import { isImageFile, isPdfOrDocxFile } from './utils';

const DocumentNotSupported = lazy(() =>
  import('./components/document-not-supported').then(module => ({
    default: module.DocumentNotSupported,
  })),
);
const ImageDocumentPreview = lazy(() =>
  import('./components/image-document-preview').then(module => ({
    default: module.ImageDocumentPreview,
  })),
);
const PdfAndDocxDocumentPreview = lazy(() =>
  import('./components/pdf-and-docx-document-preview').then(module => ({
    default: module.PdfAndDocxDocumentPreview,
  })),
);

type TPreviewDialogProps = {
  document: TDocumentPreview | null;
  onClose: () => void;
  onExternalLinkClicked?: (document: TDocumentPreview) => void;
  onDownload?: (document: TDocumentPreview) => void;
};

export function PreviewDocumentDialog({
  document,
  onClose,
  onExternalLinkClicked,
  onDownload,
}: TPreviewDialogProps) {
  return (
    <Dialog open={Boolean(document)} onOpenChange={onClose}>
      {document && (
        <DialogContent size="xl">
          {/*
            `DialogContent` requires a `DialogTitle` for the component to be accessible for screen reader users.
            And we do not have access to radix ui's VisuallyHidden wrapper here, therefore we're using a `tw-sr-only` class.
          */}
          <DialogTitle className="tw-sr-only">{document.name}</DialogTitle>
          <Suspense fallback={<Loader />}>
            {isImageFile(document.format) && (
              <ImageDocumentPreview
                document={document}
                onClose={onClose}
                onExternalLinkClicked={() => {
                  onExternalLinkClicked?.(document);
                  onClose();
                }}
              />
            )}
            {isPdfOrDocxFile(document.format) && (
              <PdfAndDocxDocumentPreview
                onClose={onClose}
                document={document}
                onExternalLinkClicked={() => {
                  onExternalLinkClicked?.(document);
                  onClose();
                }}
              />
            )}
            {!isImageFile(document.format) &&
              !isPdfOrDocxFile(document.format) && (
                <DocumentNotSupported
                  document={document}
                  onClose={onClose}
                  onDownload={() => {
                    onDownload?.(document);
                    onClose();
                  }}
                />
              )}
          </Suspense>
        </DialogContent>
      )}
    </Dialog>
  );
}
